export const factors = ["seconds", "minutes", "hours", "days"] as const;
export type Factor = (typeof factors)[number];

interface DurationFormatterProps {
  value: number; // duration in seconds
  factor?: Factor;
  shorthand?: boolean;
}

const unitMap: Record<Factor, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
};

const orderedUnits = [...factors].reverse(); // ['days', 'hours', 'minutes', 'seconds']

export function formatDuration(
  seconds: number,
  shorthand: boolean = false,
  maxUnits: number = 2
): string {
  const parts: string[] = [];

  for (const unit of orderedUnits) {
    const unitSeconds = unitMap[unit];
    const count = Math.floor(seconds / unitSeconds);
    if (count > 0) {
      const label = `${unit.slice(0, -1)}${count > 1 ? "s" : ""}`;
      parts.push(`${count}${shorthand ? label.slice(0, 1) : ` ${label}`}`);
      seconds %= unitSeconds;
    }
    if (parts.length === maxUnits) break;
  }

  const emptyLabel = shorthand ? "0 s" : "0 seconds";
  return parts.length > 0 ? parts.join(", ") : emptyLabel;
}

export function DurationFormatter({
  value,
  factor,
  shorthand = false,
}: DurationFormatterProps) {
  if (factor) {
    const unitSeconds = unitMap[factor];
    const count = Math.floor(value / unitSeconds);
    const label = factor.slice(0, -1) + (count === 1 ? "" : "s");

    if (shorthand) {
      return (
        <>
          {count}
          {label.slice(0, 1)}
        </>
      );
    }

    return (
      <>
        {count} {label}
      </>
    );
  }

  return <>{formatDuration(value, shorthand)}</>;
}
